import { createContext, useContext } from 'react';
import type { Navegacion } from '../api/operaciones';

/**
 * Permisos efectivos de la persona en el negocio activo, tal como los da `getMyNavigation` (G98, ADR-0041): cada código
 * una vez, con su ámbito («todo» o «lo suyo»; con varios roles, el más amplio, G81). Sirven **solo para ocultar o
 * desactivar botones** que van a fallar: la API comprueba cada petición y responde 403 igualmente. El panel no tiene
 * tablas propias de permisos por rol.
 */
export type Ambito = 'all' | 'own';

export interface Permisos {
    /** ¿Tiene el permiso? Con `ambito: 'all'`, solo si lo tiene con «todo» (no basta «lo suyo»). */
    puede: (codigo: string, ambito?: Ambito) => boolean;
    /** Ámbito con el que lo tiene, o null si no lo tiene. */
    ambito: (codigo: string) => Ambito | null;
    /** Los códigos de todos sus permisos efectivos (para no ofrecer más de los suyos, G37). */
    codigos: () => readonly string[];
}

export function permisosDe(navegacion: Pick<Navegacion, 'permissions'> | null): Permisos {
    const mapa = new Map<string, Ambito>((navegacion?.permissions ?? []).map((p) => [p.code, p.scope]));
    return {
        puede: (codigo, ambito) => {
            const tiene = mapa.get(codigo);
            return tiene !== undefined && (ambito !== 'all' || tiene === 'all');
        },
        ambito: (codigo) => mapa.get(codigo) ?? null,
        codigos: () => [...mapa.keys()],
    };
}

/** Sin menú cargado, ningún permiso: mejor no enseñar un botón que enseñar uno que falla. */
export const PermisosContexto = createContext<Permisos>(permisosDe(null));

/** Los permisos de la persona en el negocio activo (los pone PanelNegocio con la respuesta de getMyNavigation). */
export function usePermisos(): Permisos {
    return useContext(PermisosContexto);
}
